import React, { useCallback, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  AuthorityTermType,
  ManagerVerificationStatus,
  TManagersAndRepresentatives,
} from '../types';
import { usePositionsQuery } from '~/queries/usePositionsQuery';
import FormContainer from '~/modules/identification/components/FormComponents/FormContainer';
import PersonalInfoBlock from '~/modules/identification/components/FormComponents/PersonalInfoBlock';
import { Button, Grid, Typography } from '@mui/material';
import { EmailList } from '~/modules/identification/components/FormComponents/EmailList';
import IdentificationNumberBlock from '~/modules/identification/components/FormComponents/IdentificationNumberBlock';
import FormAutocomplete from '~/components/FormComponents/FormAutocomplete';
import { PhoneList } from '~/modules/identification/components/FormComponents/PhoneList';
import {
  signatureAuthorityDetailsOptions,
  verificationStatusDisplayNameMapping,
} from '~/modules/identification/constants';
import { AddRemoveBlock } from '~/components/AddRemoveBlock/AddRemoveBlock';
import { AssetManagerFieldTooltip } from './AssetManagerFieldTooltip';
import { TextInput } from '~/components/TextField/TextInput';
import { useAppDispatch } from '~/store/appDispatch';
import { deleteAssetManagerDocsById } from '~/store/identification-form/slices';
import FormGridItem from '~/components/FormComponents/FormGridItem';
import { TAutocompleteOption } from '~/components/AutocompleteField/types';
import { FormRadioGroup } from '~/components/FormComponents/FormRadioGroup';
import { accessAfterAuthorityExpirationOptions, authorityTermTypeOptions } from '../constants';
import { FormDateInput } from '~/components/FormComponents/FormDateInput';
import { InfoTooltip } from '~/components/InfoTooltip/InfoTooltip';
import { commonStyles } from '~/theme/styles/commonStyles';
import { useSelector } from 'react-redux';
import { selectIsReidentification } from '~/store/identification-form/selectors';

interface IAssetManagerItemProps {
  index: number;
  verificationStatus?: string | null;
  managersListLength: number;
  isAccountHolder?: boolean;
  append: () => void;
  remove: (index: number) => void;
  handleUpdateRequest: (index: number, managerId: string) => void;
  handleOpenDeleteModal: (onConfirm: () => void) => void;
}

export const AssetManagerItem = React.memo(
  ({
    index,
    managersListLength,
    isAccountHolder,
    append,
    remove,
    handleUpdateRequest,
    handleOpenDeleteModal,
  }: IAssetManagerItemProps) => {
    const dispatch = useAppDispatch();
    const { control, getValues, trigger, setValue } = useFormContext<TManagersAndRepresentatives>();
    const isReidentification = useSelector(selectIsReidentification);

    const { positionOptions } = usePositionsQuery();

    const [verificationStatus, managerId, signatureAuthorityDetails, authorityTermType, isHead] =
      useWatch({
        control,
        name: [
          `assetManagers.${index}.verificationStatus`,
          `assetManagers.${index}.id`,
          `assetManagers.${index}.signatureAuthorityDetails`,
          `assetManagers.${index}.authorityTermType`,
          `assetManagers.${index}.isHead`,
        ],
      });
    const showExpiryDate = authorityTermType === AuthorityTermType.UNTIL_DATE;

    const isDisabled = !!(verificationStatus || managerId) || Boolean(isAccountHolder);
    const isDisabledContact = isAccountHolder ? false : !!(verificationStatus || managerId);

    const isCompleted = verificationStatus === ManagerVerificationStatus.COMPLETED;
    const verificationStatusValue = !!verificationStatus
      ? verificationStatusDisplayNameMapping[
          verificationStatus as keyof typeof verificationStatusDisplayNameMapping
        ]
      : '';

    useEffect(() => {
      if (isAccountHolder && !signatureAuthorityDetails) {
        setValue(
          `assetManagers.${index}.signatureAuthorityDetails`,
          signatureAuthorityDetailsOptions[0] as TAutocompleteOption
        );
      }
    }, [isAccountHolder, signatureAuthorityDetails]);

    const handleRemove = () => {
      const removeManager = () => {
        remove(index);
        dispatch(deleteAssetManagerDocsById(managerId));
      };

      if (isCompleted) {
        handleOpenDeleteModal(removeManager);
      } else {
        removeManager();
      }
    };

    const handleTriggerFields = (fieldName: string) => {
      getValues('assetManagers')?.map((item, index) => {
        if (item[fieldName as keyof typeof item]) {
          trigger(`assetManagers.${index}.${fieldName}` as keyof TManagersAndRepresentatives);
        }
      });
    };

    const handleAuthorityTermTypeChange = useCallback(() => {
      setValue(`assetManagers.${index}.authorityExpirationDate`, null, { shouldValidate: false });
      setValue(`assetManagers.${index}.accessAfterAuthorityExpiration`, null, {
        shouldValidate: false,
      });
    }, [setValue]);

    const title = isAccountHolder
      ? !index
        ? `Керівник`
        : `Розпорядник ${index}`
      : `Розпорядник ${index + 1}`;

    return (
      <FormContainer title={title} hasBookmark>
        <PersonalInfoBlock
          section={`assetManagers.${index}`}
          isBirthDateHidden
          isDisabledForm={isDisabled}
        />
        <Grid size={{ xs: 0, sm: 0, md: 6 }} />
        <IdentificationNumberBlock section={`assetManagers.${index}`} isDisabledForm={isDisabled} />
        <PhoneList section={`assetManagers.${index}`} hideActions disabled={isDisabledContact} />
        <EmailList
          section={`assetManagers.${index}`}
          onlyOneField
          disabled={isDisabledContact}
          onBlur={() => handleTriggerFields('emailAddress')}
          label={
            <AssetManagerFieldTooltip
              label="Адреса електронної пошти"
              text="Email кожного розпорядника має бути унікальним. Вказуйте особисту електронну адресу розпорядника"
            />
          }
        />
        <FormGridItem>
          <FormAutocomplete
            options={positionOptions}
            name={`assetManagers.${index}.position`}
            disabled={isAccountHolder}
            inputProps={{
              placeholder: 'Оберіть посаду',
            }}
            label="Посада"
            shouldSaveLabel
          />
        </FormGridItem>
        <FormGridItem>
          <FormAutocomplete
            options={signatureAuthorityDetailsOptions}
            name={`assetManagers.${index}.signatureAuthorityDetails`}
            disabled={isAccountHolder}
            onBlur={() => handleTriggerFields('signatureAuthorityDetails')}
            inputProps={{
              placeholder: 'Оберіть відомості щодо наданого права підпису',
            }}
            label="Відомості щодо наданого права підпису"
          />
        </FormGridItem>
        <FormGridItem sx={{ display: 'flex' }}>
          <TextInput
            value={verificationStatusValue}
            disabled={!verificationStatus || isDisabled}
            placeholder="Очікує статус верифікації"
            label={
              <AssetManagerFieldTooltip
                label="Статус верифікації"
                text="Статус верифікації оновлюється при повторному вході в заявку, або після натиснення кнопки “Оновлення статусу”"
              />
            }
            fullWidth
            slotProps={{
              input: { readOnly: true },
            }}
          />
        </FormGridItem>
        {!isHead ? (
          <FormGridItem sx={{ alignContent: 'flex-end' }}>
            <Button
              disabled={!isCompleted}
              onClick={() => handleUpdateRequest(index, managerId || '')}
              variant="contained"
              sx={{ mb: 2 }}
            >
              Запит на оновлення даних
            </Button>
          </FormGridItem>
        ) : (
          <FormGridItem sx={{ alignContent: 'flex-end' }}></FormGridItem>
        )}
        {!isReidentification && (
          <FormGridItem>
            <FormRadioGroup
              name={`assetManagers.${index}.authorityTermType`}
              options={authorityTermTypeOptions}
              label={
                <Typography variant="body1" sx={{ ...commonStyles.centerFlexContainer, gap: 0.5 }}>
                  Термін дії повноважень
                  <InfoTooltip
                    title={
                      'Якщо повноваження розпорядника обмежені строком дії документа, на підставі якого надані повноваження (наприклад, довіреність, наказ, розпорядження), зазначте дату закінчення терміну та права доступу після її настання. Якщо строк не обмежений - оберіть “Безстроково”.'
                    }
                    placement="right"
                  />
                </Typography>
              }
              onChange={handleAuthorityTermTypeChange}
            />
            {showExpiryDate && (
              <>
                <FormDateInput
                  name={`assetManagers.${index}.authorityExpirationDate`}
                  label="Встановити дату терміну дії повноважень (по включно)"
                  placeholder="ДД.ММ.РРРР"
                  minDate={new Date() ?? null}
                />
                <FormRadioGroup
                  label="Права доступу після закінчення терміну дії повноважень"
                  name={`assetManagers.${index}.accessAfterAuthorityExpiration`}
                  options={accessAfterAuthorityExpirationOptions}
                />
              </>
            )}
          </FormGridItem>
        )}
        <Grid size={12}>
          <AddRemoveBlock
            add={append}
            remove={handleRemove}
            isPlusShown={managersListLength <= 29}
            isMinusShown={!isAccountHolder}
            plusText='Натисніть на "+" для додавання ідентифікаційних даних про осіб, які мають
            право розпоряджатися рахунками (інформація про яких внесена до переліку
            осіб, які мають право розпоряджатися рахунком і підписувати розрахункові
            документи / до картки із зразками підписів) та/або майном'
            minusText='Натисніть на "-" для видалення ідентифікаційних даних про осіб, які мають
            право розпоряджатися рахунками (інформація про яких внесена до переліку
            осіб, які мають право розпоряджатися рахунком і підписувати розрахункові
            документи / до картки із зразками підписів) та/або майном'
          />
        </Grid>
      </FormContainer>
    );
  }
);
